import { useEffect, useState, useMemo } from 'react';
import { Paragraph, Spinner } from '@hm-group/bads-react';
import type {
    ChannelType,
    Market,
    WorkspaceData,
    DepartmentLevelKpiPlanModels,
    SectionMetadataResponse,
} from '../../models';
import { ChannelType as CT } from '../../models';
import { useAppContext } from '../../context/AppContext';
import { httpGet } from '../../services/httpService';
import { environment } from '../../environments/environment';
import {
    WORKSPACE_SECTION_STORE_API,
    WORKSPACE_SECTION_ONLINE_API,
    GROUND_SECTION_API,
    SECTION_METADATA_API,
} from '../../constants';
import { Workspace } from '../Workspace/Workspace';
import { useKpiContext } from '../../context/KpiContext';
import styles from './SectionRegion.module.css';

interface SectionRegionProps {
    channelType: ChannelType;
    selectedMarkets: Market[];
}

export function SectionRegion({
    channelType,
    selectedMarkets,
}: Readonly<SectionRegionProps>) {
    const { kpiSelection, isIncludeExcitementToggleChecked, isShowGroundArticlesToggleChecked } = useKpiContext();
    const { searchBarData, setEnablePublishBtn } = useAppContext();

    const isSectionSelected = !!searchBarData.section && searchBarData.section.length > 0;
    const channelLabel = channelType === CT.Store ? 'store' : 'online';
    const isStoreWorkspace = channelType === CT.Store;

    // ── Filter markets by channel type ────────────────────────────
    const mainMarketRegions = useMemo(
        () =>
            selectedMarkets
                .filter((m) => m.isMainMarket && m.channelIntegrationKey === channelType)
                .sort((a, b) => a.sortOrder - b.sortOrder),
        [selectedMarkets, channelType]
    );

    const nonMainMarketRegions = useMemo(
        () =>
            selectedMarkets
                .filter((m) => !m.isMainMarket && m.channelIntegrationKey === channelType)
                .sort((a, b) => a.sortOrder - b.sortOrder),
        [selectedMarkets, channelType]
    );

    // ── Workspace data state ───────────────────────────────────────
    const [workspaceData, setWorkspaceData] = useState<WorkspaceData | null>(null);
    const [groundRowData, setGroundRowData] = useState<DepartmentLevelKpiPlanModels[]>([]);
    const [sectionPublishInfo, setSectionPublishInfo] = useState<SectionMetadataResponse | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // ── API endpoint selection ────────────────────────────────────
    const workspaceApiEndpoint = channelType === CT.Store
        ? WORKSPACE_SECTION_STORE_API
        : WORKSPACE_SECTION_ONLINE_API;

    // ── Fetch workspace data when section and markets change ──────
    useEffect(() => {
        const hasMarkets = mainMarketRegions.length > 0 || nonMainMarketRegions.length > 0;
        
        if (!isSectionSelected || !hasMarkets) {
            setWorkspaceData(null);
            setGroundRowData([]);
            setLoadError(null);
            return;
        }

        const sectionId = searchBarData.section;
        const brandId = searchBarData.brand;

        setEnablePublishBtn(false);
        setIsLoading(true);
        setLoadError(null);

        // Fetch main workspace data
        const workspaceUrl = `${environment.apiConfig.articleplan_api.uri}${workspaceApiEndpoint}${sectionId}`;
        
        httpGet<WorkspaceData>(workspaceUrl)
            .then((data) => {
                setWorkspaceData(data);
                setIsLoading(false);
            })
            .catch(() => {
                /* Interceptor shows error toast */
                setLoadError('Failed to load section workspace data');
                setIsLoading(false);
            });

        // Fetch ground data
        const groundUrl = `${environment.apiConfig.articleplan_api.uri}${GROUND_SECTION_API}${sectionId}/${channelType}/${brandId}`;
        
        httpGet<DepartmentLevelKpiPlanModels[]>(groundUrl)
            .then((data) => {
                setGroundRowData(data || []);
            })
            .catch(() => {
                /* Interceptor shows error toast */
                setGroundRowData([]);
            });

        // Fetch section metadata (includes publish info for all departments)
        const metadataUrl = `${environment.apiConfig.articleplan_api.uri}${SECTION_METADATA_API}${sectionId}/${channelType}`;
        
        httpGet<SectionMetadataResponse>(metadataUrl)
            .then((data) => {
                setSectionPublishInfo(data);
            })
            .catch(() => {
                /* Interceptor shows error toast */
                setSectionPublishInfo(undefined);
            });
    }, [isSectionSelected, searchBarData.section, searchBarData.brand, mainMarketRegions.length, nonMainMarketRegions.length, channelType, workspaceApiEndpoint]);

    // ── Render states ─────────────────────────────────────────────
    if (!isSectionSelected) {
        return (
            <div className={styles.initialDisplayArea}>
                <Paragraph>
                    Please select a combination to load its {channelLabel} section level Article Plan!
                </Paragraph>
            </div>
        );
    }

    if (mainMarketRegions.length === 0 && nonMainMarketRegions.length === 0) {
        return (
            <div className={styles.initialDisplayArea}>
                <Paragraph>
                    Please select a combination with valid main market for the {channelLabel} section level Article Plan!
                </Paragraph>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className={styles.initialDisplayArea}>
                <Paragraph>Error: {loadError}</Paragraph>
            </div>
        );
    }

    if (isLoading || !workspaceData) {
        return (
            <div className={styles.initialDisplayArea}>
                <Spinner size="medium" />
                <Paragraph>
                    Loading {channelLabel} section plan...
                </Paragraph>
            </div>
        );
    }

    // ── Render workspace grid ─────────────────────────────────────
    return (
        <div className={styles.workspaceContainer}>
            {/* Main market workspace */}
            {mainMarketRegions.length > 0 && (
                <div className="sticky-workspace">
                    <Workspace
                        initialGridData={workspaceData}
                        groundRowData={groundRowData}
                        readonlyPlan={true}
                        kpiConfig={kpiSelection.kpiConfig}
                        groundKpiConfig={kpiSelection.groundKpiConfig}
                        selectedPlanKpis={kpiSelection.selectedPlanKpis}
                        selectedGroundKpis={kpiSelection.selectedGroundKpis}
                        selectedRegions={mainMarketRegions}
                        isMainMarket={true}
                        showPublishedInfo={true}
                        sectionLastPublishedInfo={sectionPublishInfo}
                        isStoreWorkspaceTab={isStoreWorkspace}
                        isIncludeExcitementToggleChecked={isIncludeExcitementToggleChecked}
                        isShowGroundArticlesToggleChecked={isShowGroundArticlesToggleChecked}
                        onWeekDataChanged={() => {}}
                    />
                </div>
            )}
            
            {/* Non-main market workspace */}
            {nonMainMarketRegions.length > 0 && (
                <div className="scrollable-workspaces">
                    <Workspace
                        initialGridData={workspaceData}
                        groundRowData={groundRowData}
                        readonlyPlan={true}
                        kpiConfig={kpiSelection.kpiConfig}
                        groundKpiConfig={kpiSelection.groundKpiConfig}
                        selectedPlanKpis={kpiSelection.selectedPlanKpis}
                        selectedGroundKpis={kpiSelection.selectedGroundKpis}
                        selectedRegions={nonMainMarketRegions}
                        isMainMarket={false}
                        showPublishedInfo={true}
                        sectionLastPublishedInfo={sectionPublishInfo}
                        isStoreWorkspaceTab={isStoreWorkspace}
                        isIncludeExcitementToggleChecked={isIncludeExcitementToggleChecked}
                        isShowGroundArticlesToggleChecked={isShowGroundArticlesToggleChecked}
                        onWeekDataChanged={() => {}}
                    />
                </div>
            )}
        </div>
    );
}
